import { useWindowDimensions } from "@src/utils";
import { useMemo, useState } from "react";
import { Button, Checkbox, Icon } from "@src/components/atoms";
import AppUtils from "@src/utils/appUtils";
import { SignolUtils } from "@src/utils";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from "@tanstack/react-table";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import { dateFormatterUTC } from "@src/constants/convertions";

const getColumns = (behaviours, width, onClick, handleToggle) => {
  const isMobile = width < 768; //under 768px
  const isMobileLG = width < 1024; //under 1024px

  let columns = [
    {
      header: "Type",
      accessorKey: "eventType",
      cell: ({ row }) => (
        <Icon
          name={SignolUtils.getIndustryIcon(row.original.eventType)}
          backgroundColor="transparent"
          size="xl"
        />
      ),
    },
    {
      header: "Acronym",
      accessorKey: "acronym",
      cell: ({ row }) => (
        <span
          className={`font-bold text-lg ${row.original.enabled ? "text-gray-900" : "text-gray-600"}`}
        >
          {row.original.acronym}
        </span>
      ),
    },
    {
      header: "Behaviour Name",
      accessorKey: "name",
      cell: ({ row }) => {
        return (
          <span
            className={`font-bold ${row.original.enabled ? "text-gray-900" : "text-gray-600"}`}
          >
            {row.original.name}
            {row.original.groupAcronym && (
              <span className="font-normal text-gray-500 ml-1">
                ({row.original.groupAcronym})
              </span>
            )}
          </span>
        );
      },
    },
    {
      header: "Description",
      accessorKey: "description",
      cell: ({ row }) => (
        <div className="max-w-96 text-wrap p-2">
          <span className="italic text-xs">{row.original.description}</span>
        </div>
      ),
    },
    {
      header: "Position/Color",
      accessorKey: "position",
      cell: ({ row }) => {
        const pos = row.original.position;
        const colour = getBehaviourColourForPosition(pos);
        return (
          <div className="flex items-center justify-center">
            {pos > 0 ? (
              <span
                className={`py-1 px-3 font-bold rounded-xl text-white bg-${colour?.name}-500`}
              >
                {pos}
              </span>
            ) : (
              <span className="italic text-xs">—</span>
            )}
          </div>
        );
      },
    },
    {
      header: "Creation Date",
      accessorKey: "createdDate",
      cell: ({ row }) =>
        row.original.createdDate
          ? dateFormatterUTC(row.original.createdDate)
          : "",
    },
    {
      header: "Edit",
      cell: ({ row }) => (
        <Button
          disabled={!row.original.enabled}
          onClick={() => {
            onClick(row.original.acronym);
          }}
        >
          Edit
        </Button>
      ),
    },
    {
      header: "Add/Delete",
      accessorKey: "enabled",
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <Button
            onClick={() => handleToggle(row.original.acronym)}
            color={row.original.enabled ? "red" : "primary"}
          >
            {row.original.enabled ? "Delete" : "Add"}
          </Button>
        </div>
      ),
    },
  ];

  return columns;
};
const BehavioursTable = (props) => {
  const {
    behaviours = [],
    onBehaviourClick,
    handleToggle,
    industry = "shipping",
  } = props;
  const { width } = useWindowDimensions();
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState([]);

  const columns = useMemo(() => {
    return getColumns(behaviours, width, onBehaviourClick, handleToggle);
  }, [behaviours, width, onBehaviourClick, handleToggle]);

  const table = useReactTable({
    data: behaviours,
    columns,
    state: {
      columnVisibility,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  return !!table ? (
    <div className="p-2">
      <table className="w-full divide-y divide-gray-200 ">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-gray-500">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  className={
                    header.column.getCanSort()
                      ? "cursor-pointer select-none"
                      : ""
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-gray-200 cursor-pointer hover:bg-primary-100"
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
      <div className="h-4" />
    </div>
  ) : (
    <div>Error rendering the table</div>
  );
};

export default BehavioursTable;
